import { PlanState } from '@geartracker/types';

import { useOrganization } from 'src/api/organization.api';
import { useStats } from 'src/api/stats.api';
import { useCurrentUser } from 'src/api/user.api';
import { getActiveAuthTokens } from 'src/auth/context/jwt/auth-storage';
import { AMPLITUDE_TUNNEL } from 'src/config-global';
import { getAppVersionLabel } from 'src/utils/app-update';
import { runWhenIdle } from 'src/utils/run-when-idle';

import * as Sentry from '@sentry/react';
import type React from 'react';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';

type AmplitudeSdk = typeof import('@amplitude/analytics-browser');

// What the rest of the app may call. Deliberately narrow: the SDK is loaded
// after mount, so everything exposed here has to behave before it exists.
export type AmplitudeApi = {
  setUserId: (userId: string | undefined) => void;
};

// Analytics chunks are a frequent casualty of ad blockers, privacy extensions
// and corporate proxies. Swallowing the failure is deliberate: left unhandled,
// the rejection reaches window.onunhandledrejection, which reads « failed
// dynamic import » as a recoverable asset error and answers with a
// service-worker/cache wipe plus a full page reload (see utils/asset-recovery).
// Losing analytics is not worth rebooting the app for.
//
// A breadcrumb rather than a captured message: an SDK that a blocker refused
// to serve is the expected outcome, not a defect, and every occurrence opened
// a Sentry issue nobody could act on. It still shows up in the trail of any
// real error that follows.
const reportAnalyticsLoadFailure = (chunk: string, error: unknown) => {
  Sentry.addBreadcrumb({
    category: 'analytics',
    message: 'Amplitude SDK failed to load',
    level: 'warning',
    data: {
      chunk,
      errorMessage: error instanceof Error ? error.message : String(error),
    },
  });
};

const isStaffOrganization = (orgName: string, email: string) => {
  return (
    email.endsWith('@geartracker.net') ||
    email.endsWith('@getgeartracker.com') ||
    orgName.startsWith('geartracker') ||
    email.endsWith('@clement.pt') ||
    email.startsWith('mass.zambelli') ||
    email.startsWith('samuel.michaud') ||
    email.startsWith('spamatsam') ||
    email.startsWith('a.schmitte')
  );
};

// Define the context
export const AmplitudeContext = createContext<AmplitudeApi | null>(null);

type AmplitudeProviderProps = {
  apiKey: string;
  children: React.ReactNode;
};

// Provider interface. Analytics must never gate the app: this component is
// imported eagerly (it is tiny) and renders its children immediately, while
// the Amplitude SDK — and the queries that only feed its identify call — are
// loaded after mount.
const AmplitudeProvider = ({ apiKey, children }: AmplitudeProviderProps) => {
  // The SDK is dead weight in dev, where init is skipped anyway.
  const analyticsEnabled = !!apiKey && !import.meta.env.DEV;

  const sdkRef = useRef<AmplitudeSdk | null>(null);
  const [sdk, setSdk] = useState<AmplitudeSdk | null>(null);
  // setUserId() called before the SDK landed; replayed once it does.
  const pendingUserIdRef = useRef<{ value: string | undefined } | null>(null);

  useEffect(() => {
    if (!analyticsEnabled) return;

    let cancelled = false;
    let cancelIdleLoad = () => {};

    // Loaded from a mount effect rather than through React.lazy: as a lazy
    // provider wrapping the whole tree, this chunk (~60 kB gzip, the largest
    // on the boot path) held back the first paint.
    void (async () => {
      const amplitude = await import('@amplitude/analytics-browser');
      if (cancelled) return;

      amplitude.init(apiKey, {
        serverZone: 'EU',
        defaultTracking: true,
        serverUrl: AMPLITUDE_TUNNEL,
        // Stamps every event with the running release (`2026-09-08-abb9311`),
        // surfaced in Amplitude as "Version". Dated rather than a bare sha so
        // the breakdown shows how far behind a client is, and segmenting it by
        // orgName — already set on identify below — names the organisations
        // that are not picking up updates.
        appVersion: getAppVersionLabel(),
      });

      sdkRef.current = amplitude;
      if (pendingUserIdRef.current) {
        amplitude.setUserId(pendingUserIdRef.current.value);
        pendingUserIdRef.current = null;
      }
      setSdk(amplitude);

      // Session replay drags in rrweb (~300 KB over two chunks) on top of the
      // SDK itself, so it is pulled once the browser goes idle and added to an
      // already-initialised client. Trade-off: recording starts a beat later,
      // so the very first moments of a session are not replayed.
      cancelIdleLoad = runWhenIdle(() => {
        void import('@amplitude/plugin-session-replay-browser')
          .then(({ sessionReplayPlugin }) => {
            if (cancelled) return;

            void amplitude.add(
              sessionReplayPlugin({
                sampleRate: 1,
                // Compress replay events in a worker rather than on the main
                // thread. Off by default.
                useWebWorker: true,
                performanceConfig: {
                  // Defer compression to the browser's idle periods (default).
                  enabled: true,
                  // Coalesce bursts of inline style mutations on the same node
                  // before compression, without changing replay semantics. The
                  // data grids emit a lot of those while scrolling. Off by
                  // default.
                  mergeMutations: true,
                },
                // Capture clicks and scrolls: this is the replay data Amplitude
                // builds heatmaps from. Batched to avoid a request per click.
                interactionConfig: {
                  enabled: true,
                  batch: true,
                },
              }),
            );
          })
          .catch((error: unknown) => {
            // Replay is optional: losing its chunk costs a recording, not
            // core analytics.
            reportAnalyticsLoadFailure('session-replay', error);
          });
      });
    })().catch((error: unknown) => {
      reportAnalyticsLoadFailure('analytics-browser', error);
    });

    return () => {
      cancelled = true;
      cancelIdleLoad();
    };
  }, [analyticsEnabled, apiKey]);

  // since we are outside AuthProvider, we use presence of a token
  // to make or not the currentUser request
  const tokens = getActiveAuthTokens();
  // Nothing renders from these — they exist only to build the identify event
  // below — so they wait for the SDK instead of joining the boot request burst.
  const identifyEnabled = !!tokens?.accessToken && !!sdk;

  const { data: currentUser } = useCurrentUser(identifyEnabled);
  const { data: organization } = useOrganization(identifyEnabled);
  const { data: stats } = useStats(identifyEnabled);

  useEffect(() => {
    if (!sdk || !currentUser?.id || !organization?.id || !stats) return;

    const identifyEvent = new sdk.Identify();
    // org
    identifyEvent.setOnce('orgId', organization.id);
    identifyEvent.set('orgName', organization.name);
    identifyEvent.set('orgPlan', organization.plan);
    identifyEvent.set('orgPlanPrice', organization.planPrice || 0);
    identifyEvent.set('orgIsTrial', organization.planState === PlanState.TRIAL);
    if (isStaffOrganization(organization.name, currentUser.email || '')) {
      identifyEvent.set('isStaff', true);
    }

    // user
    identifyEvent.set('role', currentUser.role);
    if (currentUser.language) {
      identifyEvent.set('languageApp', currentUser.language);
    }

    // stats
    identifyEvent.set('orgEquipmentTotal', stats.organization.equipment.total);
    identifyEvent.set(
      'orgEquipmentConform',
      stats.organization.equipment.generalStatus.CONFORM,
    );
    identifyEvent.set(
      'orgEquipmentNotConform',
      stats.organization.equipment.generalStatus.NOT_CONFORM,
    );
    identifyEvent.set(
      'orgEquipmentDamaged',
      stats.organization.equipment.generalStatus.DAMAGED,
    );
    identifyEvent.set(
      'orgEquipmentQuarantine',
      stats.organization.equipment.generalStatus.QUARANTINE,
    );
    identifyEvent.set(
      'orgEquipmentLost',
      stats.organization.equipment.generalStatus.LOST,
    );
    identifyEvent.set(
      'orgEquipmentNotify',
      stats.organization.equipment.generalStatus.NOTIFY,
    );
    identifyEvent.set(
      'orgEquipmentTrash',
      stats.organization.equipment.generalStatus.TRASH,
    );

    identifyEvent.set('orgTagTotal', stats.organization.tag.total);
    identifyEvent.set('orgFolderTotal', stats.organization.folder.total);

    identifyEvent.set('userEquipmentTotal', stats.user.equipment.total);
    identifyEvent.set(
      'userEquipmentConform',
      stats.user.equipment.generalStatus.CONFORM,
    );
    identifyEvent.set(
      'userEquipmentNotConform',
      stats.user.equipment.generalStatus.NOT_CONFORM,
    );
    identifyEvent.set(
      'userEquipmentDamaged',
      stats.user.equipment.generalStatus.DAMAGED,
    );
    identifyEvent.set(
      'userEquipmentQuarantine',
      stats.user.equipment.generalStatus.QUARANTINE,
    );
    identifyEvent.set(
      'userEquipmentLost',
      stats.user.equipment.generalStatus.LOST,
    );
    identifyEvent.set(
      'userEquipmentNotify',
      stats.user.equipment.generalStatus.NOTIFY,
    );
    identifyEvent.set(
      'userEquipmentTrash',
      stats.user.equipment.generalStatus.TRASH,
    );
    identifyEvent.set('userUserTotal', stats.user.user.total);
    identifyEvent.set('userTagTotal', stats.user.tag.total);
    identifyEvent.set('userFolderTotal', stats.user.folder.total);

    sdk.identify(identifyEvent);
    sdk.setUserId(`user_${currentUser.id}`);
  }, [sdk, currentUser, organization, stats]);

  // Stable identity: consumers (auth-provider) hold it in callback deps.
  const api = useMemo<AmplitudeApi>(
    () => ({
      setUserId: (userId) => {
        if (sdkRef.current) {
          sdkRef.current.setUserId(userId);
          return;
        }
        pendingUserIdRef.current = { value: userId };
      },
    }),
    [],
  );

  return (
    <AmplitudeContext.Provider value={api}>
      {children}
    </AmplitudeContext.Provider>
  );
};

export default AmplitudeProvider;
